import {
  boundCheckboxField,
  BoundForm,
  boundTextAreaField,
  boundTextField,
  FormSectionLayout,
  useComputed,
} from "@homebound/beam";
import { Observer } from "mobx-react";
import { ReactNode } from "react";
import { ChangeEventReasonsBoundSelectField } from "src/components/autoPopulateSelects/ChangeEventReasonsBoundSelectField";
import { ChangeEventType, LotType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  BaseChangeEventFormInput,
  BaseChangeEventFormState,
} from "src/routes/projects/change-events/guided/baseChangeEventForm";
import { PreSignedContractUploader } from "src/routes/projects/change-events/guided/components/PreSignedContractUploader";
import { ReviewApprovers } from "src/routes/projects/change-events/guided/components/ReviewApprovers";
import { ReviewSummaryStats } from "src/routes/projects/change-events/guided/components/ReviewSummaryStats";
import { changeEventQueryFor } from "src/routes/projects/change-events/guided/utils";
import { GuidedChangeEventParams } from "src/routes/routesDef";
import { fail, queryResult } from "src/utils";
import { useReviewStepChangeEventQuery } from "./ReviewStep.generated";

type ReviewStepProps<F> = {
  formState: BaseChangeEventFormState<F>;
  /**
   * Whether Submit actually creates the change event being reviewed. Defaults to true; false when the workflow only
   * dry ran the save and applies the change some other way, so there is no change event to title or route for
   * approval, only its impact to summarize.
   */
  willCreateChangeEvent?: boolean;
  /** Passed through to the summary stats, for workflows that can project the change's schedule impact. */
  scheduleImpact?: ReactNode;
};

export function ReviewStep<F>(props: ReviewStepProps<F>) {
  const { formState, willCreateChangeEvent = true, scheduleImpact } = props;
  const { projectId } = useRouteParams<GuidedChangeEventParams>();
  const { changeEventId, fetchPolicy } = useComputed(() => changeEventQueryFor(formState), [formState]);

  const query = useReviewStepChangeEventQuery({
    variables: { changeEventId: changeEventId ?? fail("No change event id") },
    fetchPolicy,
  });

  return queryResult(query, ({ changeEvent }) => {
    const showPreSignedContract =
      willCreateChangeEvent &&
      changeEvent.type.code === ChangeEventType.External &&
      changeEvent.project.lotType.code === LotType.Boyl;

    return (
      <FormSectionLayout
        title={willCreateChangeEvent ? "Ready to Submit" : "Ready to Apply"}
        description={
          willCreateChangeEvent
            ? "Review the summary of changes below. The change event will be created on confirmation."
            : "Review the summary of changes below. This project does not have a signed contract, so the configuration will be applied immediately to the project, and no Change Event will be created."
        }
        initialFields={
          <BoundForm<BaseChangeEventFormInput>
            formState={formState}
            rows={[
              // There is nothing to describe when no change event is created, only its impact to show
              ...(willCreateChangeEvent
                ? [
                    { title: boundTextField({ label: "Title" }) },
                    {
                      reactNodeReasonId: (
                        <ChangeEventReasonsBoundSelectField
                          field={formState.reasonId}
                          label="Reason"
                          workflow={changeEvent.workflow?.code}
                        />
                      ),
                    },
                    { internalNote: boundTextAreaField({ label: "Description" }) },
                  ]
                : []),
              ...(showPreSignedContract
                ? [
                    {
                      hasPreSignedContract: boundCheckboxField({
                        fullWidth: true,
                        label: "Existing signed contract",
                        description: "Skips the homeowner approval PandaDoc. Attach the signed contract below.",
                      }),
                    },
                    {
                      reactNodePreSignedContract: (
                        <PreSignedContractUploader formState={formState} projectId={projectId} />
                      ),
                    },
                  ]
                : []),
              {
                reactNodeSummaryStats: <ReviewSummaryStats changeEvent={changeEvent} scheduleImpact={scheduleImpact} />,
              },
            ]}
          />
        }
        sections={
          willCreateChangeEvent
            ? [
                {
                  title: "Approvals",
                  description: "This change event will be sent to the following people after creation.",
                  fields: (
                    <Observer>
                      {() => (
                        <ReviewApprovers
                          // A pre-signed contract skips the client's PandaDoc, so they won't be asked to approve.
                          approvers={changeEvent.predictedApprovers.filter(
                            (a) => !(formState.hasPreSignedContract.value && a.__typename === "Client"),
                          )}
                          clientNoun={changeEvent.project.lotType.clientNoun}
                        />
                      )}
                    </Observer>
                  ),
                },
                {
                  title: "What Happens Next?",
                  description:
                    "This change event will go through the approval process. Once approved, the new line items will appear in the project scope, budget, and finish schedule.",
                },
              ]
            : []
        }
      />
    );
  });
}
